import { identity } from '@repletoryreach/sdk-api';

/**
 * Push notifications in this browser (owner, 9 October 2026), through Firebase Cloud Messaging.
 *
 * Turning them on asks the browser's permission, registers `/push-sw.js` (which shows each alert), asks Firebase
 * for this browser's token with the platform's Web Push key, and gives the token to identity. Firebase's library
 * is loaded only here, when someone turns notifications on, so it costs the rest of the console nothing.
 */

const TOKEN_KEY = 'tenant.push.token';
export const PUSH_WORKER = '/push-sw.js';

export type PushSupport = 'supported' | 'unsupported' | 'blocked';

/** Whether this browser can take push notifications, and whether its person blocked them for the console. */
export function pushSupport(): PushSupport {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return 'unsupported';
  return Notification.permission === 'denied' ? 'blocked' : 'supported';
}

/** The token this browser registered, when it did. */
export function storedPushToken(): string | null {
  try { return window.localStorage.getItem(TOKEN_KEY); } catch { return null; }
}

/** "Chrome on Windows": how this browser appears in the person's list of devices. */
export function browserLabel(ua = navigator.userAgent): string {
  const browser = /Edg\//.test(ua) ? 'Edge' : /OPR\//.test(ua) ? 'Opera' : /Firefox\//.test(ua) ? 'Firefox'
    : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : 'Browser';
  const os = /Windows/.test(ua) ? 'Windows' : /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iPhone'
    : /Mac OS X/.test(ua) ? 'Mac' : /Linux/.test(ua) ? 'Linux' : '';
  return os ? `${browser} on ${os}` : browser;
}

export class PushUnavailableError extends Error {
  constructor(public readonly reason: 'not_configured' | 'unsupported' | 'denied') {
    super(reason);
    this.name = 'PushUnavailableError';
  }
}

async function firebaseMessaging(config: identity.PushConfig) {
  const [{ initializeApp, getApps }, messaging] = await Promise.all([import('firebase/app'), import('firebase/messaging')]);
  const name = 'reach-push';
  const app = getApps().find((a) => a.name === name) ?? initializeApp({
    apiKey: config.api_key!, projectId: config.project_id!, appId: config.app_id!, messagingSenderId: config.messaging_sender_id!
  }, name);
  return { messaging: messaging.getMessaging(app), getToken: messaging.getToken, deleteToken: messaging.deleteToken, isSupported: messaging.isSupported };
}

/** Turns push on for this browser and returns the device as identity recorded it. */
export async function enablePush(): Promise<identity.PushDevice> {
  if (pushSupport() === 'unsupported') throw new PushUnavailableError('unsupported');
  const config = (await identity.getPushConfig()).data;
  if (!config.enabled) throw new PushUnavailableError('not_configured');
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new PushUnavailableError('denied');
  const registration = await navigator.serviceWorker.register(PUSH_WORKER, { scope: '/' });
  await navigator.serviceWorker.ready;
  const fb = await firebaseMessaging(config);
  if (!(await fb.isSupported())) throw new PushUnavailableError('unsupported');
  const token = await fb.getToken(fb.messaging, { vapidKey: config.vapid_key!, serviceWorkerRegistration: registration });
  const device = (await identity.registerPushDevice({ token, platform: 'web', label: browserLabel() })).data;
  try { window.localStorage.setItem(TOKEN_KEY, token); } catch { /* the device is registered; only this browser forgets which token is its own */ }
  return device;
}

/**
 * Turns push off for this browser: identity forgets the token and Firebase deletes it. Used by the switch and on
 * signing out, so the next person to sign in on a shared computer does not receive the last person's alerts.
 */
export async function disablePush(): Promise<void> {
  const token = storedPushToken();
  try { window.localStorage.removeItem(TOKEN_KEY); } catch { /* nothing kept */ }
  if (!token) return;
  try { await identity.forgetPushDevice(token); } catch { /* signed out already, or offline: Firebase's delete below still stops it */ }
  try {
    const config = (await identity.getPushConfig()).data;
    if (config.enabled) {
      const fb = await firebaseMessaging(config);
      await fb.deleteToken(fb.messaging);
    }
  } catch { /* the token is forgotten by identity, so nothing is sent to it */ }
}
